import { SeverityLabels } from '@fabric-ui/components';
import { entityProjectionNames } from '@utils/entityProjectionNames';
import { SeverityErrors, SeverityRange, SeverityRule } from './types';

export const getDisplayRange = ({ severityRange }: { severityRange: SeverityRange }) => {
  const isCritical = severityRange.severity === SeverityLabels.Critical;
  const max = isCritical ? severityRange.max : severityRange.max === 0 || !severityRange.max ? 0 : (severityRange.max - 0.01).toFixed(2);
  return `${severityRange.min} - ${max}`;
};

export const getMaxScoreByProjectionName = (projectionName: string, isNewRiskScoreEnabled = false) =>
  ({ [entityProjectionNames.Ticket]: isNewRiskScoreEnabled ? 100 : 10, [entityProjectionNames.Alert]: 100 })[projectionName] ??
  (isNewRiskScoreEnabled ? 100 : 10);

interface IGetSeverityRows {
  projectionName: string;
  rules: SeverityRule[];
  isNewRiskScoreEnabled?: boolean;
}

export const getSeverityRows = ({ projectionName, rules, isNewRiskScoreEnabled = false }: IGetSeverityRows): SeverityRange[] =>
  rules.map((rule, index) => {
    const min = rule.config.scoreGraterThanEqualFilter.num;
    const max =
      index === 0
        ? getMaxScoreByProjectionName(projectionName, isNewRiskScoreEnabled)
        : rules[index - 1].config.scoreGraterThanEqualFilter.num;
    return {
      severity: rule.name,
      min,
      max,
    };
  });

interface IHandleScoreChange {
  currentRows: SeverityRange[];
  severityToUpdate: string;
  field: 'min' | 'max';
  value: string;
}

export const handleScoreChange = ({ currentRows, severityToUpdate, field, value }: IHandleScoreChange) => {
  const severityIndexOfUpdate = currentRows.findIndex(r => r.severity === severityToUpdate);

  if (severityIndexOfUpdate === -1) {
    return currentRows;
  }

  if (value.endsWith('.') || value === '') {
    return currentRows.map((row, index) => {
      if (index === severityIndexOfUpdate) {
        return {
          ...row,
          [field]: value,
        };
      }
      return row;
    });
  }

  const newScore = Number.parseFloat(value);
  if (Number.isNaN(newScore)) {
    return currentRows;
  }

  return currentRows.reduce((newRows, currentRow, currentIndex) => {
    const isMinField = field === 'min';
    const shouldUpdateMin =
      (isMinField && currentIndex === severityIndexOfUpdate) || (!isMinField && currentIndex === severityIndexOfUpdate - 1);

    const shouldUpdateMax =
      (!isMinField && currentIndex === severityIndexOfUpdate) || (isMinField && currentIndex === severityIndexOfUpdate + 1);

    const newRow = {
      ...currentRow,
      min: shouldUpdateMin ? newScore : currentRow.min,
      max: shouldUpdateMax ? newScore : currentRow.max,
    };

    return [...newRows, newRow];
  }, [] as SeverityRange[]);
};

interface IScoreValidationCondition {
  projectionName: string;
  row: SeverityRange;
  field: 'min' | 'max';
  isNewRiskScoreEnabled?: boolean;
}

const scoreValidationCondition = ({ projectionName, row, field, isNewRiskScoreEnabled = false }: IScoreValidationCondition) =>
  (!row[field] && row[field] !== 0) ||
  row[field] < 0 ||
  row[field] > getMaxScoreByProjectionName(projectionName, isNewRiskScoreEnabled) ||
  row.min === row.max ||
  row.min > row.max;

interface IValidateScores {
  projectionName: string;
  rowsToValidate: SeverityRange[];
  isNewRiskScoreEnabled?: boolean;
}

export const validateScores = ({ projectionName, rowsToValidate, isNewRiskScoreEnabled = false }: IValidateScores): SeverityErrors =>
  rowsToValidate.reduce((acc, row) => {
    const minError = scoreValidationCondition({ projectionName, row, field: 'min', isNewRiskScoreEnabled })
      ? 'Severity thresholds must be distinct and sorted in ascending order'
      : undefined;

    const maxError = scoreValidationCondition({ projectionName, row, field: 'max', isNewRiskScoreEnabled })
      ? 'Severity thresholds must be distinct and sorted in ascending order'
      : undefined;

    const rowErrors = {
      ...(minError && { min: minError }),
      ...(maxError && { max: maxError }),
    };

    return Object.keys(rowErrors).length ? { ...acc, [row.severity]: rowErrors } : acc;
  }, {} as SeverityErrors);

export const findSeverityScoreInRange = ({ ranges, score }: { ranges: SeverityRange[]; score: number }) =>
  ranges.find(item => {
    if (item.severity === SeverityLabels.Critical) {
      return score >= item.min && score <= item.max;
    }
    if (item.severity === SeverityLabels.Low) {
      return score < item.max;
    }
    return score >= item.min && score < item.max;
  });

export const getSeverityByScoreV2 = (ranges: SeverityRange[], score = 0): SeverityLabels | null => {
  const severity = findSeverityScoreInRange({ ranges, score });
  if (!severity) {
    return null;
  }
  return severity?.severity;
};

export const severityRowsGridSx = {
  display: 'grid',
  gridTemplateColumns: '150px 315px 350px 1fr',
  gap: 5,
  py: 2,
  pl: 3,
  pr: 4,
  mb: 1,
  border: '1px solid var(--color-border-base-primary)',
  alignItems: 'center',
  borderRadius: '8px',
  backgroundColor: 'var(--color-surface-base-primary)',
};
